import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Globe, Plus, ArrowLeft, Building2, Mail, Link, Key, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { schoolApi } from '../api/schoolApi';
import { useToastStore } from '@/shared/stores/toastStore';

// Validation Schema
const createSchoolSchema = z.object({
    name: z.string().min(3, 'School name must be at least 3 characters'),
    admin_email: z.string().email('Invalid email address'),
    supabase_url: z.string().url('Invalid Supabase URL'),
    supabase_anon_key: z.string().min(10, 'Supabase Anon Key is required'),
    service_role_key: z.string().min(10, 'Service Role Key is required'),
    db_password: z.string().min(6, 'Database Password must be at least 6 characters'),
    admin_password: z.string().min(6, 'Admin Password must be at least 6 characters'),
});

type CreateSchoolFormData = z.infer<typeof createSchoolSchema>;

export const CreateSchool = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showAnonKey, setShowAnonKey] = useState(false);
    const [showServiceRoleKey, setShowServiceRoleKey] = useState(false);
    const [showDbPassword, setShowDbPassword] = useState(false);
    const [showAdminPassword, setShowAdminPassword] = useState(false);
    const { showToast } = useToastStore();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<CreateSchoolFormData>({
        resolver: zodResolver(createSchoolSchema),
    });

    const onSubmit = async (data: CreateSchoolFormData) => {
        setIsLoading(true);
        try {
            const response = await schoolApi.createSchool(data);
            if (response.success) {
                showToast('School created successfully!', 'success');
                navigate('/super-admin/tenants');
            } else {
                showToast(response.error || 'Failed to create school', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Manage Schools', path: '/super-admin/tenants', icon: <Globe size={14} /> },
                    { label: 'Register School' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Register New School"
                    subtitle="Set up a new school instance with its own database credentials."
                    icon={<Building2 size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '800px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)',
                backdropFilter: 'blur(10px)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="School Name"
                                placeholder="e.g. Green Valley High School"
                                leftIcon={<Building2 size={18} />}
                                error={errors.name?.message}
                                {...register('name')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Admin Email"
                                type="email"
                                placeholder="admin@school.com"
                                leftIcon={<Mail size={18} />}
                                error={errors.admin_email?.message}
                                {...register('admin_email')}
                                required
                                helperText="The initial administrator account will be created with this email."
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2', marginTop: '1rem' }}>
                            <h3 style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                // marginBottom: '1rem',
                                color: 'var(--text-primary)',
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                <ShieldCheck size={18} color="var(--accent-primary)" style={{ marginRight: '0.5rem' }} />
                                Supabase Configuration
                            </h3>
                            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', }}>
                                These credentials are used to connect the school to its dedicated database instance.
                            </p>
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Supabase URL (API URL)"
                                placeholder="https://your-project.supabase.co"
                                leftIcon={<Link size={18} />}
                                error={errors.supabase_url?.message}
                                {...register('supabase_url')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Supabase Anon Key (Anonpublic)"
                                type={showAnonKey ? 'text' : 'password'}
                                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <div onClick={() => setShowAnonKey(!showAnonKey)} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
                                        {showAnonKey ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.supabase_anon_key?.message}
                                {...register('supabase_anon_key')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Service Role Key (Service_role)"
                                type={showServiceRoleKey ? 'text' : 'password'}
                                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                                leftIcon={<ShieldCheck size={18} />}
                                rightIcon={
                                    <div onClick={() => setShowServiceRoleKey(!showServiceRoleKey)} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
                                        {showServiceRoleKey ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.service_role_key?.message}
                                {...register('service_role_key')}
                                required
                                helperText="Used for administrative tasks. Keep this secure."
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Database Password"
                                type={showDbPassword ? 'text' : 'password'}
                                placeholder="Your database password..."
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <div onClick={() => setShowDbPassword(!showDbPassword)} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
                                        {showDbPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.db_password?.message}
                                {...register('db_password')}
                                required
                                helperText="PostgreSQL database password."
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Admin Initial Password"
                                type={showAdminPassword ? 'text' : 'password'}
                                placeholder="Set admin password..."
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <div onClick={() => setShowAdminPassword(!showAdminPassword)} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
                                        {showAdminPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.admin_password?.message}
                                {...register('admin_password')}
                                required
                                helperText="The initial password for the administrator account."
                            />
                        </div>
                    </div>

                    <div style={{
                        marginTop: '3rem',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem',
                        borderTop: '1px solid var(--border-color)',
                        paddingTop: '2rem'
                    }}>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            onClick={() => navigate(-1)}
                            disabled={isLoading}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="filled"
                            color="primary"
                            loading={isLoading}
                        >
                            Create School Instance
                        </CustomButton>
                    </div>
                </form>
            </div>
        </div>
    );
};
